import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { applyBlockOps } from '@/lib/exercise-v2/generation-client';
import { buildDuplicateBlockOps, type SourceBlock } from '@/lib/exercise-v2/duplicate';

export function useDeleteExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (exerciseId: string) => {
      const { error } = await supabase.from('exercises').delete().eq('id', exerciseId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, exerciseId) => {
      qc.invalidateQueries({ queryKey: ['tutor-exercises'] });
      qc.invalidateQueries({ queryKey: ['admin-exercises'] });
      qc.removeQueries({ queryKey: ['exercise-detail', exerciseId] });
    },
  });
}

/** Duplique un exercice (ligne + blocs V2). Renvoie l'id de la copie. */
async function duplicateV2Exercise(exerciseId: string, userId: string | null): Promise<string> {
  const { data: original, error } = await supabase
    .from('exercises')
    .select('*')
    .eq('id', exerciseId)
    .single();
  if (error || !original) throw new Error(error?.message ?? 'Exercice introuvable');
  const o = original as Record<string, unknown>;

  // `exercise_blocks` est postérieure aux types générés (stale volontaire).
  const db = supabase as unknown as SupabaseClient;
  const { data: blocks, error: blocksErr } = await db
    .from('exercise_blocks')
    .select('id, parent_id, block_type, section, position, data')
    .eq('exercise_id', exerciseId);
  if (blocksErr) throw new Error(blocksErr.message);

  const copyRow: Record<string, unknown> = {
    chapter_id: o.chapter_id ?? null,
    lesson_id: o.lesson_id ?? null,
    level: o.level ?? null,
    created_by: userId,
    title: `${String(o.title)} (copie)`,
    statement_md: o.statement_md,
    context_md: o.context_md ?? null,
    difficulty: o.difficulty,
    estimated_minutes: o.estimated_minutes,
    step_by_step_mode: o.step_by_step_mode ?? false,
    requires_calculator: o.requires_calculator ?? false,
    graph_config: o.graph_config ?? null,
    visual_blocks: o.visual_blocks ?? [],
    is_fundamental: o.is_fundamental ?? false,
    fundamental_mode: o.fundamental_mode ?? null,
    illustration_url: o.illustration_url ?? null,
    content_version: 'v2',
    status: 'draft',
  };
  const { data: copy, error: copyErr } = await supabase
    .from('exercises')
    .insert(copyRow as never)
    .select('id')
    .single();
  if (copyErr || !copy) throw new Error(copyErr?.message ?? 'Échec duplication');

  try {
    await applyBlockOps(
      copy.id,
      buildDuplicateBlockOps((blocks ?? []) as unknown as SourceBlock[]),
    );
  } catch (e) {
    await supabase.from('exercises').delete().eq('id', copy.id);
    throw e instanceof Error ? e : new Error('Échec duplication des blocs');
  }
  return copy.id;
}

export function useDuplicateExercise() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (exerciseId: string) => {
      // Zéro V1 : tout exercice est en V2 — copie de la ligne + des blocs.
      return duplicateV2Exercise(exerciseId, user?.id ?? null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tutor-exercises'] });
      qc.invalidateQueries({ queryKey: ['admin-exercises'] });
    },
  });
}

export function useUpdateExerciseStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      exerciseId,
      status,
    }: {
      exerciseId: string;
      status: 'draft' | 'published' | 'archived';
    }) => {
      const { error } = await supabase.from('exercises').update({ status }).eq('id', exerciseId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-exercises'] });
      qc.invalidateQueries({ queryKey: ['admin-exercises'] });
      qc.invalidateQueries({ queryKey: ['exercise-detail', variables.exerciseId] });
    },
  });
}
